import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FiMinus, FiPlus, FiShoppingCart, FiX } from 'react-icons/fi';
import SiteNav, { SHOP_NAV_LINKS } from '../components/SiteNav';
import useNavSolid from '../hooks/useNavSolid';
import useCart from '../hooks/useCart';
import { createCheckout, fetchShopProducts } from '../api/shop';
import { LIBRARY_BY_ID } from '../data/library';
import abcColoringBookThumb from '../assets/abc-coloring-book-thumb.png';
import { PUBLIC_SITE_ORIGIN } from '../config';

const COVER_BY_SLUG = {
  'abc-coloring': abcColoringBookThumb,
};

function formatMoney(cents, currency = 'usd') {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(
    cents / 100
  );
}

function CheckoutBanner() {
  const params = new URLSearchParams(window.location.search);
  const status = params.get('checkout');
  if (status === 'success') {
    return (
      <p className="shop-banner shop-banner--success" role="status">
        Thank you! Your order is confirmed. We&apos;ll be in touch if we need anything.
      </p>
    );
  }
  if (status === 'canceled') {
    return (
      <p className="shop-banner shop-banner--muted" role="status">
        Checkout was canceled. Your cart is still here when you&apos;re ready.
      </p>
    );
  }
  return null;
}

export default function ShopPage() {
  const navSolid = useNavSolid(24);
  const cart = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [email, setEmail] = useState('');

  const productBySlug = useMemo(() => Object.fromEntries(products.map(p => [p.slug, p])), [products]);

  const lineDetails = useMemo(() => {
    return cart.items
      .map(item => {
        const product = productBySlug[item.productSlug];
        if (!product) return null;
        return {
          ...item,
          product,
          lineTotal: product.priceCents * item.quantity,
        };
      })
      .filter(Boolean);
  }, [cart.items, productBySlug]);

  const cartTotalCents = lineDetails.reduce((sum, line) => sum + line.lineTotal, 0);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const list = await fetchShopProducts();
      setProducts(list);
    } catch (e) {
      setError(e.message || 'Failed to load shop.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProducts();
    document.title = "Libby's Pop-Up Shop — Libby the Lion & Friends";
    const canonical = `${PUBLIC_SITE_ORIGIN}/shop`;
    document.querySelector('link[rel="canonical"]')?.setAttribute('href', canonical);
    document.querySelector('meta[property="og:url"]')?.setAttribute('content', canonical);
  }, [loadProducts]);

  const handleCheckout = async () => {
    if (lineDetails.length === 0) return;
    setCheckoutLoading(true);
    setError('');
    try {
      const { checkoutUrl } = await createCheckout({
        items: cart.items,
        customerEmail: email.trim() || undefined,
      });
      window.location.href = checkoutUrl;
    } catch (e) {
      setError(e.message || 'Checkout failed.');
      setCheckoutLoading(false);
    }
  };

  return (
    <div className="v2-shell shop-shell">
      <SiteNav solid={navSolid} links={SHOP_NAV_LINKS} brandHref="/" />
      <header className="shop-hero">
        <div className="shop-hero__inner">
          <p className="section-eyebrow">Official shop</p>
          <h1 className="section-display">Libby&apos;s Pop-Up Shop</h1>
          <p className="shop-hero__lead">Get the latest deals here.</p>
        </div>
      </header>

      <main className="shop-main">
        <CheckoutBanner />
        {error && (
          <p className="shop-banner shop-banner--error" role="alert">
            {error}
          </p>
        )}

        <div className="shop-layout">
          <section className="shop-grid-section" aria-labelledby="shop-products-heading">
            <h2 id="shop-products-heading" className="visually-hidden">
              Products
            </h2>
            {loading && <p className="shop-muted">Loading products…</p>}
            {!loading && products.length === 0 && (
              <p className="shop-muted">No products available right now.</p>
            )}
            <ul className="shop-product-grid">
              {products.map(product => {
                const library = LIBRARY_BY_ID[product.slug];
                const cover = COVER_BY_SLUG[product.slug] || library?.cover;
                const tagline = library?.tagline || product.description;
                return (
                  <li key={product.slug} className="shop-product-card">
                    {cover && (
                      <img
                        src={cover}
                        alt={library?.coverAlt || product.name}
                        className="shop-product-card__cover"
                        width={176}
                        height={264}
                        decoding="async"
                      />
                    )}
                    <div className="shop-product-card__body">
                      <h3 className="shop-product-card__title">{product.name}</h3>
                      {tagline && <p className="shop-product-card__tagline">{tagline}</p>}
                      <p className="shop-product-card__price">{formatMoney(product.priceCents, product.currency)}</p>
                      <button
                        type="button"
                        className="btn-buy-amazon shop-add-btn"
                        onClick={() => cart.addItem(product.slug, 1)}
                      >
                        Add to cart
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>

          </section>

          <button
            type="button"
            className="shop-cart-fab"
            aria-label={`Open cart, ${cart.itemCount} items`}
            onClick={() => cart.setDrawerOpen(true)}
          >
            <FiShoppingCart size={22} aria-hidden />
            {cart.itemCount > 0 && <span className="shop-cart-fab__badge">{cart.itemCount}</span>}
          </button>
        </div>
      </main>

      <aside
        className={`shop-cart-drawer${cart.drawerOpen ? ' is-open' : ''}`}
        aria-hidden={!cart.drawerOpen}
        aria-label="Shopping cart"
      >
        <div className="shop-cart-drawer__backdrop" onClick={() => cart.setDrawerOpen(false)} aria-hidden />
        <div className="shop-cart-drawer__panel" role="dialog" aria-modal="true">
          <header className="shop-cart-drawer__head">
            <h2 className="shop-cart-drawer__title">Your cart</h2>
            <button
              type="button"
              className="shop-cart-drawer__close"
              aria-label="Close cart"
              onClick={() => cart.setDrawerOpen(false)}
            >
              <FiX size={22} />
            </button>
          </header>

          {lineDetails.length === 0 ? (
            <p className="shop-muted shop-cart-drawer__empty">Your cart is empty.</p>
          ) : (
            <>
              <ul className="shop-cart-lines">
                {lineDetails.map(({ productSlug, product, quantity, lineTotal }) => (
                  <li key={productSlug} className="shop-cart-line">
                    <div className="shop-cart-line__info">
                      <span className="shop-cart-line__name">{product.name}</span>
                      <span className="shop-cart-line__price">{formatMoney(lineTotal, product.currency)}</span>
                    </div>
                    <div className="shop-cart-line__qty">
                      <button
                        type="button"
                        className="shop-qty-btn"
                        aria-label="Decrease quantity"
                        onClick={() => cart.setQuantity(productSlug, quantity - 1)}
                      >
                        <FiMinus size={16} />
                      </button>
                      <span aria-live="polite">{quantity}</span>
                      <button
                        type="button"
                        className="shop-qty-btn"
                        aria-label="Increase quantity"
                        onClick={() => cart.setQuantity(productSlug, quantity + 1)}
                      >
                        <FiPlus size={16} />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
              <p className="shop-cart-total">
                Total <strong>{formatMoney(cartTotalCents)}</strong>
              </p>
              <label className="shop-email-label">
                Email (optional)
                <input
                  type="email"
                  className="shop-email-input"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                />
              </label>
              <button
                type="button"
                className="btn-buy-amazon shop-checkout-btn"
                disabled={checkoutLoading || lineDetails.length === 0}
                onClick={handleCheckout}
              >
                {checkoutLoading ? 'Redirecting…' : 'Checkout with Stripe'}
              </button>
            </>
          )}
        </div>
      </aside>

      <footer className="v2-footer shop-footer">
        <div className="footer-title-band">
          <p className="footer-line">
            <a href="/">← Back to Libby the Lion &amp; Friends</a>
          </p>
        </div>
      </footer>
    </div>
  );
}
